"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { Button } from "@/components/ui/button"
import { STEPS } from "@/lib/breach/steps"
import { buildStepPages } from "@/lib/breach/step-pages"
import { getWorkspace, listWorkspaces, saveWorkspace, storageAvailable } from "@/lib/breach/storage"
import type { Workspace } from "@/lib/breach/workspace"
import { IncidentShell, type ResolvedView } from "./incident-shell"
import { InfoPanelProvider } from "./info-panel"
import { useAccount } from "./account"
import { cloudGet, cloudList, cloudPut, planSync } from "@/lib/breach/cloud"
import { INCIDENT_VIEW_IDS, incidentHref, type IncidentNav, type IncidentViewId } from "./incident-nav"

function isViewId(x: string | null): x is IncidentViewId {
  return x !== null && (INCIDENT_VIEW_IDS as readonly string[]).includes(x)
}

/** Work out which view the URL asks for, with sane fallbacks for bad params. */
function resolveView(params: URLSearchParams, w: Workspace): ResolvedView {
  const raw = params.get("v")
  const v: IncidentViewId = isViewId(raw) ? raw : w.introSeen ? "index" : "intro"
  if (v === "harden") { const n = Number.parseInt(params.get("p") ?? "0", 10); return { v: "harden", p: Number.isNaN(n) ? 0 : Math.min(Math.max(n, 0), 7) } }
  if (v === "intro") return { v: "intro", p: params.get("p") === "1" ? 1 : 0 }
  if (v === "step") {
    const step = STEPS.find((s) => s.id === params.get("step"))
    if (!step) return { v: "index" }
    const pages = buildStepPages(step, w.intake)
    const rawP = Number.parseInt(params.get("p") ?? "0", 10)
    const p = Number.isNaN(rawP) ? 0 : Math.min(Math.max(rawP, 0), pages.length - 1)
    return { v: "step", step, p }
  }
  return { v }
}

/**
 * Container for /incident: loads the workspace from this browser, resolves the
 * view from query params (?id&v&step&p), and renders the shell. All state
 * changes persist immediately through saveWorkspace.
 */
export function IncidentView() {
  const params = useSearchParams()
  const router = useRouter()
  const id = params.get("id")
  const [w, setW] = useState<Workspace | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [persistOk, setPersistOk] = useState(true)
  const { me } = useAccount()
  const [syncing, setSyncing] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const signedIn = !!me?.user

  useEffect(() => {
    setPersistOk(storageAvailable())
    const found = id ? getWorkspace(id) : listWorkspaces()[0] ?? null
    setW(found)
    setLoaded(true)
  }, [id])

  const signedInRef = useRef(false)
  signedInRef.current = signedIn

  /** Debounced save to the account so rapid edits send one request. */
  const scheduleCloudSave = useCallback((next: Workspace) => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    setSyncing(true)
    saveTimer.current = setTimeout(async () => {
      const r = await cloudPut(next)
      if (r.ok) setSyncError(null)
      else if (r.reason === "conflict" && r.conflict) { saveWorkspace(r.conflict); setW(r.conflict); setSyncError(null) }
      else setSyncError(r.reason === "limit" ? "You've reached the limit of saved incidents." : r.reason === "auth" ? "You've been signed out." : "Couldn't save to your account just now.")
      setSyncing(false)
    }, 800)
  }, [])

  // After sign-in (or on load while signed in), merge this browser's incidents with the account's: newest wins, nothing is deleted.
  useEffect(() => {
    if (!signedIn || !loaded) return
    let cancelled = false
    void (async () => {
      setSyncing(true)
      const remote = await cloudList()
      if (!remote || cancelled) { setSyncing(false); if (!remote) setSyncError("Couldn't reach your account just now."); return }
      const local = listWorkspaces()
      const plan = planSync(local, remote)
      for (const lid of plan.upload) { const lw = local.find((x) => x.id === lid); if (lw) await cloudPut(lw) }
      for (const rid of plan.download) { const rw = await cloudGet(rid); if (rw) saveWorkspace(rw) }
      if (cancelled) return
      setSyncError(null); setSyncing(false)
      const fresh = id ? getWorkspace(id) : listWorkspaces()[0] ?? null
      if (fresh) setW(fresh)
    })()
    return () => { cancelled = true }
  }, [signedIn, loaded, id])

  const update = useCallback((fn: (w: Workspace) => Workspace) => {
    setW((prev) => {
      if (!prev) return prev
      const next = fn(prev)
      setPersistOk(saveWorkspace(next))
      if (signedInRef.current) scheduleCloudSave(next)
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If the same incident is open in another tab, pick up its changes so the two never overwrite each other.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== "aab.workspaces.v1" || !id) return
      const fresh = getWorkspace(id)
      if (fresh) setW((prev) => (prev && fresh.updatedAt > prev.updatedAt ? fresh : prev))
    }
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [id])

  const view = useMemo(() => (w ? resolveView(params, w) : null), [params, w])

  const go = useCallback(
    (nav: IncidentNav) => {
      if (w) router.push(incidentHref(w.id, nav))
    },
    [router, w],
  )

  if (!loaded) return null
  if (!w || !view) {
    return (
      <div className="mx-auto max-w-xl text-center">
        <h1 className="text-2xl font-bold">No incident found on this device</h1>
        <p className="mt-3 text-muted-foreground">Progress is saved only in the browser you started in. Start a new one, or import a file you exported earlier.</p>
        <div className="mt-6"><Link href="/start"><Button size="lg" className="bg-accent-solid text-accent-solid-foreground">Start a new incident</Button></Link></div>
      </div>
    )
  }

  return (
    <InfoPanelProvider>
      <IncidentShell w={w} update={update} persistOk={persistOk} view={view} go={go} syncing={syncing} syncError={syncError} />
    </InfoPanelProvider>
  )
}
